define('ordersDetailCompositionProofsheetView',[
  'app',
  'module',
  'marionette',
  'underscore',
  'pdfView',
  'settings',
  'modalsConfirmationView',
  'dropzone',
  'imageUtils',
  'compuzzProUtils',
  'pdfEditorView',
  'ordersDetailCompositionProofsheetHistoryView',
  'entities/pim_product',
  'template!ordersDetailCompositionProofsheetView'
], function (
  App,
  Module,
  Marionette,
  _,
  PdfView,
  Settings,
  ConfirmationView,
  Dropzone,
  ImageUtils,
  CompuzzProUtils,
  PdfEditorView,
  HistoryView,
  PimProduct,
  Tpl
) {
  'use strict';

  // Absolute http(s) URL pointing at an image the editor can insert.
  var IMAGE_URL_RE = /^https?:\/\/[^\s"'<>]+\.(png|jpe?g|gif|bmp|webp|svg)(\?[^\s"'<>]*)?$/i;

  // Walks an arbitrary JSON tree and collects every distinct image URL it finds.
  // PIM products keep picture URLs scattered across nested relations (supplier
  // data, reseller data, variations, print areas…), so rather than chasing each
  // known field we just parse the whole payload.
  function collectImageUrls(node, urls, seen) {
    if (!node) {
      return;
    }
    if (typeof node === 'string') {
      if (IMAGE_URL_RE.test(node) && !seen[node]) {
        seen[node] = true;
        urls.push(node);
      }
      return;
    }
    if (_.isArray(node) || _.isObject(node)) {
      _.each(node, function (value) {
        collectImageUrls(value, urls, seen);
      });
    }
  }

  function fileNameFromUrl(url) {
    var clean = url.split('?')[0].split('#')[0];
    var segment = clean.substring(clean.lastIndexOf('/') + 1);
    return segment || 'image';
  }

  // The editor draws inserted images onto a <canvas>, so a cross-origin image
  // whose host sends no CORS headers would taint the canvas and break the PDF
  // export. Route any URL on a different origin than the dashboard through the
  // backend proxy (served from a compuzz origin with CORS), and leave
  // same-origin / relative URLs untouched.
  function proxiedImageUrl(rawUrl) {
    if (!rawUrl || !/^https?:\/\//i.test(rawUrl)) {
      return rawUrl;
    }
    var origin = window.location.protocol + '//' + window.location.host;
    if (rawUrl === origin || rawUrl.indexOf(origin + '/') === 0) {
      return rawUrl;
    }
    // Settings.url does not encode param values, so encode the URL ourselves.
    return Settings.url('compuzz2', 'urlproxy', { url: encodeURIComponent(rawUrl) });
  }

  // Turns a fetched PIM product into image-source-picker items. Each item only
  // needs a `.get(key)` accessor; `url` is absolute (proxied when cross-origin)
  // so the picker uses it as-is instead of resolving it as a file-server link.
  function extractPimProductImages(pimProduct) {
    var urls = [];
    collectImageUrls(pimProduct.toJSON(), urls, {});
    return _.map(urls, function (url) {
      var attrs = { url: proxiedImageUrl(url), fileName: fileNameFromUrl(url) };
      return {
        get: function (key) {
          return attrs[key];
        }
      };
    });
  }

  Module.exports = Marionette.LayoutView.extend({
    template: Tpl,
    className: 'orders-detail-body-composition-proofsheet-view',

    ui: {
      content: '.content',
      imageContainer: '.image-ctn',
      loader: '.loader-ctn',
      uploadButtonContainer: '.content .upload-button-container',
      uploadButton: '.buttons .upload-button',
      uploadInput: '.content .upload-input',
      deleteButton: '.content .delete-button',
      zoomButton: '.content .zoom-button',
      downloadButton: '.content .download-button',
      copyButton: '.content .copy-button'
    },

    regions: {
      historyRegion: '.history-region'
    },

    modelEvents: {
      'change:proofsheetFileUid': 'render'
    },

    events: {
      'click .btn-edit-custom-proofsheet': 'onEditCustomProofsheet',
      'click .btn-edit-custom-proofsheet-inline': 'onEditInline',
      'click .btn-create-custom-proofsheet-inline': 'onCreateInline',
      'click @ui.deleteButton': 'onDelete',
      'click @ui.uploadButtonContainer': 'onUpload',
      'click @ui.uploadButton': 'onUpload',
      'click @ui.downloadButton': 'onDownload',
      'click @ui.zoomButton': 'onZoom'
    },

    serializeData: function () {
      var data = {};

      data.src = '';

      if (!!Settings.configValue('advancedMode')) {
        data.advancedMode = true;
      } else {
        data.advancedMode = false;
      }

      data.hasProofsheet = !!this.model.get('proofsheetFileUid');

      if(this.model.get('proofsheetFileUid'))
        data.src = Settings.url('file', this.model.get('proofsheetFileUid')) + '&action=preview';

      return data;
    },

    onRender: function () {
      this.historyView = new HistoryView({
        model: this.model
      });
      this.getRegion('historyRegion').show(this.historyView);

      //----------
      if(this.model.get('proofsheetFileUid')) {
        this.ui.loader.css('display', '');
        this.ui.imageContainer.css('display', 'flex');
        this.ui.uploadButtonContainer.css('display', 'none');
      } else {
        this.ui.loader.css('display', '');
        this.ui.imageContainer.css('display', 'none');
        this.ui.uploadButtonContainer.css('display', 'flex');
      }

      //----------
      var that = this;

      this.ui.content.dropzone({
        url: Settings.url('compuzz'),
        autoProcessQueue: true,
        uploadMultiple: false,
        acceptedFiles: 'application/pdf',
        init: function () {
          this.on('addedfile', function(file) {
            that.showLoader(true);

            var formdata = new FormData();
            formdata.append('file', file);
            formdata.append('filename', file.name);
            var fileInfo = {
              fileType: file.type,
              fileExt: file.name.substring( file.name.lastIndexOf( '.' ) ),
              fileName: file.name,
              fileLastModified: file.lastModified,
              formData: formdata,
              base64Value: ''
            };
            App.request('order:upload-custom-proofsheet', that.model.get('purchaseOrderId'), fileInfo).then(_.bind(function () {
              CompuzzProUtils.getInstance().trigger('notificationCenter:compuzzProRefreshComposition');
              that.model.fetch();
            }, this));

          });
        },
        dictDefaultMessage: _.i18n('common.file.drag.drop')
      });
    },

    onEditCustomProofsheet: function () {
      this.trigger('editCustomProofsheet');
    },

    onEditInline: function () {
      var uid = this.model.get('proofsheetFileUid');
      if (!uid)
        return;

      var poi = this.model.get('purchaseOrderId');
      PdfEditorView.open({
        id: poi,
        url: Settings.url('file', uid),
        fileName: poi + '.pdf',
        title: _.i18n('pdfEditor.title', null, 'PDF editor') + ': ' + poi,
        imageSources: this.buildImageSources(),
        referenceImages: this.buildReferenceImages(),
        onSave: _.bind(this.uploadEditedProofsheet, this)
      });
    },

    onCreateInline: function () {
      var poi = this.model.get('purchaseOrderId');
      PdfEditorView.open({
        id: poi,
        blank: true,
        fileName: poi + '.pdf',
        title: _.i18n('pdfEditor.createTitle', null, 'Create proofsheet') + ': ' + poi,
        imageSources: this.buildImageSources(),
        referenceImages: this.buildReferenceImages(),
        onSave: _.bind(this.uploadEditedProofsheet, this)
      });
    },

    // The print-area preview thumbnails shown in the editor as read-only
    // reference images, so the user can see where the logo sits on the product
    // while editing the proofsheet. These are the print-zone pictures (the bare
    // product photos with the print area marked), the same images the
    // composition tab renders as `.printarea-preview-thumb` — not the composited
    // snapshot. The pictures are fetched by the parent composition tab and
    // handed down via options.pzPictures (keyed by print-zone id).
    buildReferenceImages: function () {
      var compositions = this.model.get('compositions') || [];
      var pzPictures = this.options.pzPictures || {};
      var refs = [];
      _.each(compositions, function (compo) {
        if (!compo) {
          return;
        }
        var pictures = pzPictures[compo.pzId] || [];
        var label = compo.paName ? (compo.paCode + ' - ' + compo.paName) : (compo.paCode || '');
        _.each(pictures, function (picture) {
          if (picture && picture.imageUrl) {
            refs.push({ url: picture.imageUrl, label: label });
          }
        });
      });
      return refs;
    },

    // Image sources for the editor's "insert image" picker: the order's own
    // attachments plus (when resolvable from the order) the customer's and the
    // product's attachments. Models are fetched lazily and cached per source.
    buildImageSources: function () {
      var order = this.model;
      var sources = [];

      sources.push({
        key: 'order',
        label: _.i18n('pdfEditor.source.order', null, 'Order'),
        loader: function (first, pageSize) {
          return order.getAttachments(first, pageSize);
        }
      });

      var endUserId = order.get('endUserId');
      if (endUserId) {
        var customer = App.request('customers:entity:end-user-id', endUserId);
        var customerReady = null;
        sources.push({
          key: 'customer',
          label: _.i18n('pdfEditor.source.customer', null, 'Customer'),
          loader: function (first, pageSize) {
            if (!customerReady)
              customerReady = customer.fetch();
            return customerReady.then(function () {
              return customer.getAttachments(first, pageSize);
            });
          }
        });
      }

      var productSecId = order.get('productSecId');
      if (productSecId) {
        if (order.get('pim')) {
          // PIM products have no attachment endpoint; their pictures live as
          // URLs inside the product payload, so fetch it once and scrape them.
          var pimProduct = App.request('pim:products:model', { id: productSecId });
          var pimReady = null;
          var pimImages = [];
          sources.push({
            key: 'product',
            label: _.i18n('pdfEditor.source.product', null, 'Product'),
            loader: function (first, pageSize) {
              if (!pimReady) {
                pimReady = pimProduct.fetch().then(function () {
                  pimImages = extractPimProductImages(pimProduct);
                });
              }
              return pimReady.then(function () {
                return pimImages.slice(first, first + pageSize);
              });
            }
          });
        } else {
          var product = App.request('products-rel:model', { secId: productSecId });
          var productReady = null;
          sources.push({
            key: 'product',
            label: _.i18n('pdfEditor.source.product', null, 'Product'),
            loader: function (first, pageSize) {
              if (!productReady)
                productReady = product.fetch();
              return productReady.then(function () {
                return product.getAttachments(first, pageSize);
              });
            }
          });
        }
      }

      return sources;
    },

    uploadEditedProofsheet: function (file) {
      this.showLoader(true);

      var formdata = new FormData();
      formdata.append('file', file);
      formdata.append('filename', file.name);

      App.request('order:upload-custom-proofsheet', this.model.get('purchaseOrderId'), {
        formData: formdata
      }).then(_.bind(function () {
        CompuzzProUtils.getInstance().trigger('notificationCenter:compuzzProRefreshComposition');
        this.model.fetch();
      }, this));
    },

    onUpload: function () {
      this.ui.content.click();
    },

    showLoader: function () {
      this.ui.imageContainer.css('display', 'none');
      this.ui.uploadButtonContainer.css('display', 'none');
      this.ui.loader.css('display', 'flex');
    },

    onDownload: function () {
      var url = Settings.url('file', this.model.get('proofsheetFileUid'));
      if(url === '')
        return;

      var fileName = this.model.get('purchaseOrderId') + '.pdf';

      var anchor = document.createElement('a');
      anchor.href = url;
      anchor.target = '_blank';
      anchor.download = fileName;
      anchor.click();
    },

    onDelete: function () {
      var uid = this.model.get('proofsheetFileUid');
      if(uid === '')
        return;

      ConfirmationView.prototype.open({
        text: _.i18n('orders.detail.composition.proofsheet.delete.confirmation'),
        onConfirm: _.bind(function () {
          App.request('order:delete-custom-proofsheet', this.model.get('purchaseOrderId')).then(_.bind(function () {
            this.model.fetch();
          }, this));
        }, this)
      });
    },

    onZoom: function () {
      PdfView.prototype.open({
        url: Settings.url('file', this.model.get('proofsheetFileUid')),
        title: 'Proofsheet: ' + this.model.get('purchaseOrderId')
      });
    }
  });
});

